import { vatBreakdown, vatConfig } from "../lib/legal";
import { formatEuros } from "../lib/money";

/**
 * VAT disclosure line rendered near a displayed price/total. The treatment is
 * driven entirely by `vatConfig` (see lib/legal.ts → brand/theme.json), so the
 * operator sets the real rate without any code change.
 *
 * Prices are always VAT-INCLUSIVE: checkout charges exactly the displayed total
 * (createPayment sends only {quote_id, qty}), so there is no path that adds VAT
 * on top at checkout (HFE-1). An operator whose prices are ex-VAT must bake VAT
 * into the price itself.
 *
 * While `vatConfig.confirmed` is false the rate is honestly flagged as an
 * unconfirmed placeholder — this is scaffolding, not a tax claim.
 */
export function PriceDisclosure({
  totalCents,
  testid = "vat-disclosure",
}: {
  totalCents: number;
  testid?: string;
}) {
  const { ratePercent, confirmed } = vatConfig;

  // Until the operator confirms the VAT treatment, do NOT assert a concrete
  // rate or euro figure to the customer (DLEG-1) — a definite-but-wrong VAT
  // claim at real checkout is worse than none. Mirror the placeholder
  // discipline of the legal pages: withhold the affirmative claim, show a
  // clearly non-committal line the shopper can read as "not final".
  if (!confirmed) {
    // Soft-launch: do not advertise "VAT not configured" at the pay button,
    // and do not invent a rate (DLEG-1). The charged amount is the total
    // already shown — checkout never adds tax on top (HFE-1).
    return (
      <p className="text-xs text-muted-fg" data-testid={testid}>
        The amount shown is the total you will be charged.
      </p>
    );
  }

  const { vatCents } = vatBreakdown(totalCents, ratePercent);
  return (
    <p className="text-xs text-muted-fg" data-testid={testid}>
      Price includes VAT ({ratePercent}%) — incl. {formatEuros(vatCents)} VAT.
    </p>
  );
}
